import { messaging, VAPID_KEY, getToken, onMessage } from '../config/firebase';
import apiClient from './apiClient';

class PushNotificationService {
  private fcmToken: string | null = null;

  /**
   * Request notification permission and get FCM token
   */
  async requestPermission(): Promise<boolean> {
    try {
      if (!messaging) {
        console.warn('Push notifications not supported in this browser');
        return false;
      }

      // Check if permission is already granted
      if (Notification.permission === 'granted') {
        await this.registerToken();
        return true;
      }

      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        await this.registerToken();
        return true;
      } else {
        console.log('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Register service worker and get FCM token
   */
  private async registerToken(): Promise<void> {
    try {
      if (!messaging) return;

      // Wait for existing service worker to be ready
      let registration = await navigator.serviceWorker.ready.catch(() => null);
      
      if (!registration) {
        // Register service worker if not already registered
        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/',
          updateViaCache: 'none'
        });
        console.log('Service worker registered:', registration);
        
        // Wait for activation
        if (registration.installing) {
          await new Promise((resolve) => {
            registration!.installing!.addEventListener('statechange', function() {
              if (this.state === 'activated') {
                resolve(null);
              }
            });
          });
        }
      } else {
        console.log('Using existing service worker:', registration);
        // Update service worker if needed
        await registration.update().catch(err => {
          console.warn('Service worker update check failed:', err);
        });
      }

      // Get FCM token
      // const token = await getToken(messaging, {
      //   vapidKey: VAPID_KEY,
      //   serviceWorkerRegistration: registration
      // });

      // if (token) {
      //   console.log('FCM token obtained:', token.substring(0, 20) + '...');
      //   this.fcmToken = token;

      //   // Send token to backend
      //   await this.subscribeToBackend(token);
      // } else {
      //   console.log('No FCM token available');
      // }
    } catch (error) {
      console.error('Error getting FCM token:', error);
    }
  }

  /**
   * Subscribe FCM token with backend API
   */
  private async subscribeToBackend(token: string): Promise<void> {
    try {
      const client = apiClient.getSellerClient();
      await client.post('/pushnotification/subscribe', {
        fcmToken: token,
        deviceType: 'web',
        deviceName: navigator.userAgent.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser',
        orderNotificationsEnabled: true,
        invoiceNotificationsEnabled: true
      });

      console.log('Successfully subscribed to push notifications');
      localStorage.setItem('fcm_token', token);
    } catch (error) {
      console.error('Error subscribing to backend:', error);
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<void> {
    try {
      const token = this.fcmToken || localStorage.getItem('fcm_token');
      if (!token) {
        console.log('No FCM token to unsubscribe');
        return;
      }

      const client = apiClient.getSellerClient();
      await client.post('/pushnotification/unsubscribe', {
        fcmToken: token
      });

      console.log('Successfully unsubscribed from push notifications');
      localStorage.removeItem('fcm_token');
      this.fcmToken = null;
    } catch (error) {
      console.error('Error unsubscribing:', error);
    }
  }

  /**
   * Setup foreground message listener
   */
  setupForegroundListener(callback: (payload: any) => void): void {
    if (!messaging) return;

    // onMessage(messaging, (payload) => {
    //   console.log('Foreground message received:', payload);

    //   // Show browser notification for foreground messages
    //   if (Notification.permission === 'granted') {
    //     const notificationTitle = payload.notification?.title || 'Taminzo Notification';
    //     const notificationOptions = {
    //       body: payload.notification?.body || 'You have a new notification',
    //       icon: '/logo192.png',
    //       badge: '/badge-72x72.png',
    //       data: payload.data,
    //       tag: payload.data?.orderNumber || 'taminzo-notification'
    //     };

    //     const notification = new Notification(notificationTitle, notificationOptions);

    //     // Handle click on foreground notification
    //     notification.onclick = (event) => {
    //       event.preventDefault();
    //       const url = payload.data?.url || '/orders';
    //       window.location.href = url;
    //       notification.close();
    //     };
    //   }

    //   // Call the callback for app-specific handling
    //   callback(payload);
    // });
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return messaging !== null && 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    return Notification.permission;
  }

  /**
   * Get subscription status from backend
   */
  async getStatus(): Promise<any> {
    try {
      const client = apiClient.getSellerClient();
      const response = await client.get('/pushnotification/status');
      return response.data;
    } catch (error) {
      console.error('Error getting push notification status:', error);
      return null;
    }
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(orderNotifications: boolean, invoiceNotifications: boolean): Promise<void> {
    try {
      const token = this.fcmToken || localStorage.getItem('fcm_token');
      if (!token) {
        console.log('No FCM token for updating preferences');
        return;
      }

      const client = apiClient.getSellerClient();
      await client.put('/pushnotification/preferences', {
        fcmToken: token,
        orderNotificationsEnabled: orderNotifications,
        invoiceNotificationsEnabled: invoiceNotifications
      });

      console.log('Successfully updated preferences');
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  }
}

export const pushNotificationService = new PushNotificationService();
